import { LoadingOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

import { isPartOfEPICLoginFlow } from '../../pages/epic/epicLoginUtils';
import { useAuthStore } from '../../stores/authStore';
import { getOrCreateAddonCommunicator, OverlayProtocolTypes } from '../../utils/addon/addon-communicator';
import { addCurrentTimeUnixToLocalStorage, inIframe } from '../../utils/common';
import { NEXT_URL_QUERY_PARAMS, REDIRECTING_TO_SUMMARY_TIME } from '../../utils/consts';
import { getOrCreateLogger } from '../../utils/logger';
import { PageContainerWithoutBackground } from '../PageContainerWithoutBackground';

function isPathUrlQueryParam(path: string): boolean {
	return path.startsWith('?');
}

function getRedirectSource(localStorageNextUrl: string | null, queryParamsNextUrl: string | null): string {
	if (localStorageNextUrl) {
		return 'localStorage';
	}

	if (queryParamsNextUrl) {
		return 'queryParams';
	}

	return 'currentLocation';
}

function getPathnameForLog(url: string): string {
	try {
		return new URL(url, globalThis.location.origin).pathname;
	} catch {
		return url.split('?')[0] ?? url;
	}
}

export const GuestOnlyRoute = observer(function GuestOnlyRoute() {
	const authStore = useAuthStore();
	const location = useLocation();

	const [loggedIn, setLoggedIn] = useState(authStore?.isLoggedIn);

	useEffect((): void => setLoggedIn(authStore?.isLoggedIn), [authStore?.isLoggedIn, authStore]);

	if (loggedIn === undefined || authStore.isInitializing) {
		return (
			<PageContainerWithoutBackground>
				<Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
			</PageContainerWithoutBackground>
		);
	}

	if (loggedIn) {
		let nextUrl = `${location.pathname}${location.search}`;
		nextUrl = nextUrl.replace('/login', '');
		const queryParamsNextUrl = new URLSearchParams(location.search).get(NEXT_URL_QUERY_PARAMS);
		const localStorageNextUrl = localStorage.getItem(NEXT_URL_QUERY_PARAMS);

		if (isPartOfEPICLoginFlow()) {
			addCurrentTimeUnixToLocalStorage(REDIRECTING_TO_SUMMARY_TIME);
		}

		if (localStorageNextUrl) {
			nextUrl = localStorageNextUrl;
			localStorage.removeItem(NEXT_URL_QUERY_PARAMS);

			const isInIframe = inIframe();

			if (isInIframe) {
				getOrCreateAddonCommunicator().sendToAddon(OverlayProtocolTypes['addon-change-frame-src'], {
					url: `${window.location.origin}${nextUrl}`,
				});
			}
		} else if (queryParamsNextUrl) {
			nextUrl = queryParamsNextUrl;
		}

		const redirectSource = getRedirectSource(localStorageNextUrl, queryParamsNextUrl);
		const fromPath = location.pathname;
		const fromHasQuery = location.search.length > 0;

		if (isPathUrlQueryParam(nextUrl)) {
			const redirectUrl = `/${nextUrl}`;
			getOrCreateLogger().info('GuestOnlyRoute: redirecting authenticated user from login to main app', {
				fromPath,
				fromHasQuery,
				toPath: getPathnameForLog(redirectUrl),
				toHasQuery: redirectUrl.includes('?'),
				source: redirectSource,
			});
			return <Navigate to={redirectUrl} />;
		}

		getOrCreateLogger().info('GuestOnlyRoute: redirecting authenticated user from login to main app', {
			fromPath,
			fromHasQuery,
			toPath: getPathnameForLog(nextUrl),
			toHasQuery: nextUrl.includes('?'),
			source: redirectSource,
		});
		return <Navigate to={nextUrl} />;
	}

	return <Outlet />;
});
